import React, { useState, useEffect } from 'react';
import { Button, Box, Typography, CircularProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import examApis from '../../../apis/exams';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect';
import { SnackbarSeverityType } from '../../../enums';
import pallet from '../../../layouts/pallet';
import { SchoolClassesConfig } from '../../../types/schoolConfigType';
import { StyledButton, ViewEditWrapper } from '../styled';
import {
  ExamResponse,
  SubjectResponse,
  Subject,
  ScoringType,
  GradeScale
} from '../types';
import { getViewEditExamModalColumns } from '../utils/viewEditExamModalColumns';
import AddSubjectModal from './AddSubjectModal';
import EditSubjectModal from './EditSubjectModal';
import { dataGridSx } from '../../common/styled/DataGrid';
import Icons from '../../../components/icons';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';

interface ViewEditExamModalProps {
  open: boolean;
  onClose: () => void;
  onExamUpdated: () => void;
  exam?: ExamResponse;
  academicYearOptions: { value: string; label: string }[];
  termOptions: { name: string; id: string }[];
  allClasses?: SchoolClassesConfig;
  school?: string;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
}

const ViewEditExamModal: React.FC<ViewEditExamModalProps> = ({
  open,
  onClose,
  onExamUpdated,
  exam,
  academicYearOptions,
  termOptions,
  allClasses,
  school,
  showToast
}) => {
  const [currentExam, setCurrentExam] = useState<ExamResponse | undefined>(
    exam
  );
  const [isAddSubjectModalOpen, setIsAddSubjectModalOpen] = useState(false);
  const [isEditSubjectModalOpen, setIsEditSubjectModalOpen] = useState(false);
  const [isDeleteSubjectModalOpen, setIsDeleteSubjectModalOpen] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [subjectToEdit, setSubjectToEdit] = useState<
    SubjectResponse | undefined
  >(undefined);

  const [subjectIdToDelete, setSubjectIdToDelete] = useState<
    string | undefined
  >(undefined);

  const [fetchedScoringTypes, setFetchedScoringTypes] = useState<ScoringType[]>(
    []
  );
  const [isLoadingScoringTypes, setIsLoadingScoringTypes] = useState(false);

  const [fetchedGradeScales, setFetchedGradeScales] = useState<GradeScale[]>(
    []
  );
  const [isLoadingGradeScales, setIsLoadingGradeScales] = useState(false);

  const [fetchedSubjects, setFetchedSubjects] = useState<Subject[]>([]);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(false);

  useEffect(() => {
    setCurrentExam(exam);
  }, [exam]);

  useEffect(() => {
    if (open && school && currentExam?.grade && currentExam?.division) {
      setIsLoadingSubjects(true);
      examApis
        .getSubjects(school, currentExam.grade, currentExam.division)
        .then((response: any) => {
          const apiSubjects = Array.isArray(response)
            ? response
            : response.data || [];
          setFetchedSubjects(apiSubjects);
        })
        .catch((error) => {
          console.error(error);
          showToast('Failed to load subjects.', SnackbarSeverityType.error);
        })
        .finally(() => {
          setIsLoadingSubjects(false);
        });
    }
  }, [open, school, currentExam?.grade, currentExam?.division, showToast]);

  useEffect(() => {
    if (open && school) {
      setIsLoadingGradeScales(true);
      examApis
        .getGradeScales(school)
        .then((response: any) => {
          const apiGradeScales = Array.isArray(response)
            ? response
            : response.data || [];
          setFetchedGradeScales(apiGradeScales);
        })
        .catch((error) => {
          console.error(error);
          showToast('Failed to load grade scales.', SnackbarSeverityType.error);
        })
        .finally(() => {
          setIsLoadingGradeScales(false);
        });
    }
  }, [open, school, showToast]);

  useEffect(() => {
    if (open && school) {
      setIsLoadingScoringTypes(true);
      examApis
        .getScoringTypes(school)
        .then((response: any) => {
          const apiScoringTypes = Array.isArray(response)
            ? response
            : response.data || [];
          setFetchedScoringTypes(apiScoringTypes);
        })
        .catch((error) => {
          console.error(error);
          showToast(
            'Failed to load scoring types.',
            SnackbarSeverityType.error
          );
        })
        .finally(() => {
          setIsLoadingScoringTypes(false);
        });
    }
  }, [open, school, showToast]);

  if (!currentExam) {
    return null;
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCurrentExam((prev) => (prev ? { ...prev, [name]: value } : prev));
  };

  const handleTermChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } })
  ) => {
    const selectedTerm = termOptions.find(
      (t) => t.id === (e.target.value as string)
    );
    setCurrentExam((prev) =>
      prev ? { ...prev, termId: selectedTerm?.id || '' } : prev
    );
  };

  const handleAddSubject = (subjectData: {
    subject: Subject;
    scoringType: ScoringType;
    gradeScale?: GradeScale;
    maxMarks?: number;
  }) => {
    const newSubject: SubjectResponse = {
      subject: subjectData.subject,
      scoringType: subjectData.scoringType,
      ...(subjectData.gradeScale ? { gradeScale: subjectData.gradeScale } : {}),
      ...(typeof subjectData.maxMarks === 'number'
        ? { maxMarks: subjectData.maxMarks }
        : {})
    } as SubjectResponse;

    setCurrentExam((prev) =>
      prev ? { ...prev, subjects: [...prev.subjects, newSubject] } : prev
    );
  };

  const handleUpdateSubject = (updatedSubject: SubjectResponse) => {
    setCurrentExam((prev) =>
      prev
        ? {
            ...prev,
            subjects: prev.subjects.map((subject) =>
              subject.subject.id === updatedSubject.subject.id
                ? updatedSubject
                : subject
            )
          }
        : prev
    );
  };

  const handleDeleteSubjectModalOpen = (id: string) => {
    setIsDeleteSubjectModalOpen(true);
    setSubjectIdToDelete(id);
  };

  const handleDeleteSubjectCancel = () => {
    setIsDeleteSubjectModalOpen(false);
    setSubjectIdToDelete(undefined);
  };

  const handleDeleteSubject = () => {
    if (!subjectIdToDelete) {
      handleDeleteSubjectCancel();
      return;
    }
    setCurrentExam((prev) =>
      prev
        ? {
            ...prev,
            subjects: prev.subjects.filter(
              (s) => s.subject.id !== subjectIdToDelete
            )
          }
        : prev
    );
    handleDeleteSubjectCancel();
  };

  const handleSave = async () => {
    if (!currentExam || !school) {
      console.error('Missing exam data or school information for saving.');
      return;
    }

    const subjectsPayload = currentExam.subjects.map((subject) => {
      const base: any = {
        subjectId: subject.subject.id || '',
        scoringType: subject.scoringType.id || '',
        maxMarks: subject.maxMarks
      };
      if (subject.gradeScale?.id) {
        base.gradeScaleId = subject.gradeScale.id;
      }
      return base;
    });

    const payload = {
      name: currentExam.name,
      termId: currentExam.termId,
      subjects: subjectsPayload
    };

    try {
      setIsSaving(true);
      await examApis.updateExam(school, currentExam.id, payload);
      showToast('Exam saved successfully!', SnackbarSeverityType.success);
      onClose();
      onExamUpdated();
    } catch (error) {
      console.error('Error saving exam:', error);
      showToast('Failed to save exam.', SnackbarSeverityType.error);
    } finally {
      setIsSaving(false);
    }
  };

  const isSaveButtonDisabled = isSaving;

  const columns = getViewEditExamModalColumns(
    setSubjectToEdit,
    handleDeleteSubjectModalOpen,
    setIsEditSubjectModalOpen
  );

  return (
    <React.Fragment>
      <DialogModal
        open={open}
        onClose={onClose}
        title='View/Edit Exam'
        maxWidth={false}
        PaperPropsSx={{
          width: '100vw',
          height: '100vh',
          m: 1,
          maxHeight: 'calc(100vh - 32px)',
          maxWidth: 'calc(100vw - 32px)',
          borderRadius: '16px'
        }}
        actions={
          <Box sx={{ display: 'flex', gap: '16px', marginRight: '28px' }}>
            <Button
              onClick={onClose}
              color='primary'
              sx={{ border: `1px solid ${pallet.primary}` }}
            >
              Cancel
            </Button>
            <StyledButton onClick={handleSave} disabled={isSaveButtonDisabled}>
              {isSaving ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Save Changes'
              )}
            </StyledButton>
          </Box>
        }
      >
        <Box
          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}
        >
          <ViewEditWrapper>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <TextInputWithIcon
                label='Academic Year'
                placeholder='Select Academic Year'
                name='academicYear'
                value={currentExam.academicYear}
                onChange={handleInputChange}
                fullWidth
                disabled
              />
              <TextInputWithIcon
                label='Exam Name'
                placeholder='Enter Exam Name'
                name='name'
                value={currentExam.name}
                onChange={handleInputChange}
                fullWidth
              />
              <DropdownSelect
                label='Term'
                placeholder='Select Term'
                name='termId'
                value={currentExam.termId}
                options={termOptions.map((t) => ({
                  value: t.id,
                  label: t.name
                }))}
                onChange={handleTermChange}
                disabled={termOptions.length === 0}
                fullWidth
              />
            </Box>
            <StyledButton onClick={() => setIsAddSubjectModalOpen(true)}>
              <Icons.PlusWhite style={{ marginRight: '6px' }} /> Add Subject
            </StyledButton>
          </ViewEditWrapper>
          <Box sx={{ width: '100%' }}>
            <DataGrid
              rows={currentExam.subjects.map((s, idx) => ({
                id: s.subject.id || idx,
                subjectName: s.subject.name,
                scoringType: s.scoringType.name,
                maxMarks:
                  typeof s.maxMarks === 'number' && !Number.isNaN(s.maxMarks)
                    ? s.maxMarks
                    : '',
                gradeScale: s.gradeScale?.name || '',
                raw: s
              }))}
              columns={columns}
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 }
                }
              }}
              sx={{
                ...dataGridSx(pallet),
                '& .MuiDataGrid-virtualScroller': {
                  overflowY: 'auto'
                }
              }}
            />
          </Box>
          <Typography variant='body2' sx={{ mt: 1, textAlign: 'right' }}>
            {currentExam.subjects.length} result
            {currentExam.subjects.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </DialogModal>

      <AddSubjectModal
        open={isAddSubjectModalOpen}
        onClose={() => setIsAddSubjectModalOpen(false)}
        onAddSubject={handleAddSubject}
        subjectOptions={fetchedSubjects}
        school={school}
        scoringTypeOptions={fetchedScoringTypes}
        isLoadingScoringTypes={isLoadingScoringTypes}
        gradeScaleOptions={fetchedGradeScales}
        isLoadingGradeScales={isLoadingGradeScales}
        isLoadingSubjects={isLoadingSubjects}
        existingSubjects={currentExam.subjects.map((s) => s.subject)}
        showToast={showToast}
      />

      <EditSubjectModal
        open={isEditSubjectModalOpen}
        onClose={() => setIsEditSubjectModalOpen(false)}
        subjectToEdit={subjectToEdit}
        onUpdateSubject={handleUpdateSubject}
        school={school}
        scoringTypeOptions={fetchedScoringTypes}
        isLoadingScoringTypes={isLoadingScoringTypes}
        gradeScaleOptions={fetchedGradeScales}
        isLoadingGradeScales={isLoadingGradeScales}
        subjectOptions={fetchedSubjects}
        isLoadingSubjects={isLoadingSubjects}
        showToast={showToast}
      />

      <ConfirmationDialog
        open={isDeleteSubjectModalOpen}
        onClose={handleDeleteSubjectCancel}
        message={`Are you sure you want to delete the selected subject?`}
        onConfirm={handleDeleteSubject}
      />
    </React.Fragment>
  );
};

export default ViewEditExamModal;
